import { Alert, AlertIcon, Badge, Box, Button, Flex, Text, Tooltip } from '@chakra-ui/react'
import { useAuth } from '@hb/react'
import {
  CervicalExam,
  Contraction,
  FetalHeartRateExam,
  FHRDeceleration,
  LaborEntry,
  LaborEntryType,
  LaborVitalSigns,
  MedicationLog,
  Narrative,
  NarrativeCategory,
  Procedure,
  WithId,
} from '@hb/shared'
import { useCallback, useMemo, useState } from 'react'
import { LockIcon } from '../../../../icons/LockIcon'
import { useLaborCharting } from '../context'
import { ReportBirthModal } from '../ReportBirthModal'

// ── Helpers ──────────────────────────────────────────────────

const LOCAL_STORAGE_KEY_PREFIX = 'hb_laborTimeline_sortDesc_'

const formatDateTime = (ts: number) =>
  new Date(ts).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

// ── Per-type summary lines ────────────────────────────────────

const getEntrySummary = (entry: WithId<LaborEntry<LaborEntryType>>): string => {
  switch (entry.type) {
    case LaborEntryType.VITALS: {
      const d = entry.data as LaborVitalSigns
      const parts: string[] = []
      if (d.bloodPressure) parts.push(`BP ${d.bloodPressure.systolic}/${d.bloodPressure.diastolic}`)
      if (d.pulse) parts.push(`HR ${d.pulse}`)
      if (d.temperature) parts.push(`Temp ${d.temperature}°F`)
      if (d.o2Saturation) parts.push(`O₂ ${d.o2Saturation}%`)
      return parts.join(' · ') || 'Vitals'
    }
    case LaborEntryType.CERVICAL_EXAM: {
      const d = entry.data as CervicalExam
      const parts: string[] = []
      if (d.dilation != null) parts.push(`${d.dilation} cm`)
      if (d.effacement != null) parts.push(`${d.effacement}%`)
      if (d.station != null) parts.push(`Station ${d.station > 0 ? '+' : ''}${d.station}`)
      return parts.join(' · ') || 'Cervical Exam'
    }
    case LaborEntryType.FHR: {
      const d = entry.data as FetalHeartRateExam
      const parts: string[] = []
      if (d.baselineBpm) parts.push(`${d.baselineBpm} bpm`)
      if (d.variability) parts.push(d.variability)
      if (d.decelerations && d.decelerations !== FHRDeceleration.NONE)
        parts.push(`${d.decelerations} decels`)
      return parts.join(' · ') || 'FHR'
    }
    case LaborEntryType.CONTRACTION: {
      const d = entry.data as Contraction
      const parts: string[] = []
      if (d.frequency) parts.push(`Q${d.frequency}min`)
      if (d.duration) parts.push(`${d.duration}s`)
      if (d.intensity) parts.push(d.intensity)
      return parts.join(' · ') || 'Contraction'
    }
    case LaborEntryType.MEDICATION: {
      const d = entry.data as MedicationLog
      return d.medName || 'Medication'
    }
    case LaborEntryType.NARRATIVE: {
      const d = entry.data as Narrative
      if (d.category === NarrativeCategory.EMERGENCY) return '⚠ Emergency note'
      return d.noteBody
        ? d.noteBody.slice(0, 80) + (d.noteBody.length > 80 ? '…' : '')
        : 'Narrative'
    }
    case LaborEntryType.PROCEDURE: {
      const d = entry.data as Procedure
      return d.procedureName || 'Procedure'
    }
    default:
      return ''
  }
}

const ENTRY_TYPE_COLOR: Record<LaborEntryType, string> = {
  [LaborEntryType.VITALS]: 'blue',
  [LaborEntryType.CERVICAL_EXAM]: 'purple',
  [LaborEntryType.FHR]: 'green',
  [LaborEntryType.CONTRACTION]: 'orange',
  [LaborEntryType.MEDICATION]: 'red',
  [LaborEntryType.NARRATIVE]: 'gray',
  [LaborEntryType.PROCEDURE]: 'teal',
}

const ENTRY_TYPE_LABELS: Record<LaborEntryType, string> = {
  [LaborEntryType.VITALS]: 'Vitals',
  [LaborEntryType.CERVICAL_EXAM]: 'Cervical Exam',
  [LaborEntryType.FHR]: 'FHR',
  [LaborEntryType.CONTRACTION]: 'Contraction',
  [LaborEntryType.MEDICATION]: 'Medication',
  [LaborEntryType.NARRATIVE]: 'Narrative',
  [LaborEntryType.PROCEDURE]: 'Procedure',
}

// Alert for late/prolonged decelerations
const hasCriticalFHR = (entry: WithId<LaborEntry<LaborEntryType>>): boolean => {
  if (entry.type !== LaborEntryType.FHR) return false
  const d = entry.data as FetalHeartRateExam
  return d.decelerations === FHRDeceleration.LATE || d.decelerations === FHRDeceleration.PROLONGED
}

// ── Entry card ────────────────────────────────────────────────

const EntryCard = ({
  entry,
  onSelect,
}: {
  entry: WithId<LaborEntry<LaborEntryType>>
  onSelect: () => void
}) => {
  const isCritical = hasCriticalFHR(entry)
  const colorScheme = ENTRY_TYPE_COLOR[entry.type]
  const summary = getEntrySummary(entry)
  const hasVersionHistory = (entry.versionHistory?.length ?? 0) > 0
  const createdTs = entry.createdOn ?? entry.startedAt
  const modifiedTs = entry.updatedOn

  return (
    <Box
      bg="white"
      _dark={{ bg: 'gray.800' }}
      borderRadius={6}
      boxShadow="0 1px 3px #00000018"
      borderLeft="3px solid"
      borderLeftColor={`${colorScheme}.400`}
      overflow="hidden"
      cursor="pointer"
      onClick={onSelect}
      _hover={{ boxShadow: '0 2px 6px #00000022' }}
      transition="box-shadow 0.15s">
      <Flex align="flex-start" gap={2} px={3} pt={2.5} pb={2}>
        <Flex flex={1} minW={0} direction="column" gap={1}>
          {/* Type badge + amendment badge */}
          <Flex align="center" gap={1.5} flexWrap="wrap">
            <Badge
              colorScheme={colorScheme}
              fontFamily="Hero-New"
              fontSize="xs"
              borderRadius="full"
              px={2}>
              {ENTRY_TYPE_LABELS[entry.type]}
            </Badge>
            {isCritical && (
              <Badge
                colorScheme="red"
                fontFamily="Hero-New"
                fontSize="xs"
                borderRadius="full"
                px={2}>
                ⚠ FHR Alert
              </Badge>
            )}
            {hasVersionHistory && (
              <Badge
                colorScheme="orange"
                variant="outline"
                fontFamily="Hero-New"
                fontSize="xs"
                borderRadius="full"
                px={2}>
                Amended
              </Badge>
            )}
          </Flex>

          {/* Summary */}
          <Text
            fontSize="sm"
            fontFamily="Hero-New"
            color="gray.700"
            _dark={{ color: 'gray.200' }}
            noOfLines={2}>
            {summary}
          </Text>

          {/* Dual timestamps */}
          <Flex align="center" gap={3} flexWrap="wrap" mt={0.5}>
            <Flex align="center" gap={1}>
              <Tooltip
                label="Created — locked at first save, never changes"
                hasArrow
                placement="top">
                <Flex align="center" gap={1} cursor="help">
                  <LockIcon />
                  <Text fontSize="xs" color="gray.400" fontFamily="Hero-New">
                    Created: {formatDateTime(createdTs)}
                  </Text>
                </Flex>
              </Tooltip>
            </Flex>
            {modifiedTs && modifiedTs !== createdTs && (
              <Text fontSize="xs" color="gray.400" fontFamily="Hero-New">
                Modified: {formatDateTime(modifiedTs)}
              </Text>
            )}
          </Flex>
        </Flex>
      </Flex>
      {isCritical && (
        <Alert status="error" borderRadius={0} py={1} px={3} fontSize="xs">
          <AlertIcon boxSize={3} />
          Late or prolonged decelerations documented — non-blocking clinical alert
        </Alert>
      )}
    </Box>
  )
}

// ── Main timeline ─────────────────────────────────────────────

export const LaborEntriesTimeline = () => {
  const {
    laborEntries,
    stage,
    laborOnsetAt,
    deliveredOn,
    minimumDeliveryReport,
    handleNewEntry,
    handleNewEntryType,
    selectEntry,
    reportBirthTapped,
  } = useLaborCharting()
  const { authUser } = useAuth()
  const userId = authUser?.uid ?? 'anon'

  const [sortDesc, setSortDesc] = useState<boolean>(() => {
    try {
      return localStorage.getItem(`${LOCAL_STORAGE_KEY_PREFIX}${userId}`) !== 'asc'
    } catch {
      return true
    }
  })

  const [reportBirthOpen, setReportBirthOpen] = useState(false)

  const handleSortToggle = useCallback(() => {
    setSortDesc(prev => {
      const next = !prev
      try {
        localStorage.setItem(`${LOCAL_STORAGE_KEY_PREFIX}${userId}`, next ? 'desc' : 'asc')
      } catch {
        /* localStorage may be unavailable (private browsing) — ignore */
      }
      return next
    })
  }, [userId])

  const sortedEntries = useMemo(
    () => (sortDesc ? [...laborEntries] : [...laborEntries].reverse()),
    [laborEntries, sortDesc],
  )

  const isCompleted = !!deliveredOn && deliveredOn !== Infinity
  const isActive = stage === 'labor'

  // §4.1: Post-Birth Note opens a NARRATIVE entry only
  const handleNewEntryAction = useCallback(() => {
    if (isCompleted) {
      handleNewEntryType(LaborEntryType.NARRATIVE)
    } else {
      handleNewEntry()
    }
  }, [isCompleted, handleNewEntry, handleNewEntryType])

  const handleOpenReportBirth = useCallback(() => {
    reportBirthTapped()
    setReportBirthOpen(true)
  }, [reportBirthTapped])

  return (
    <Flex direction="column" w="100%" h="100%">
      {/* Header bar */}
      <Flex
        align="center"
        px={3}
        py={2}
        borderBottom="1px solid"
        borderColor="gray.100"
        bg="white"
        _dark={{ bg: 'gray.800', borderBottomColor: 'whiteAlpha.100' }}
        shrink={0}
        gap={2}>
        <Text
          fontFamily="Hero-New"
          fontWeight={700}
          fontSize="sm"
          color="gray.600"
          _dark={{ color: 'gray.300' }}>
          {sortedEntries.length} {sortedEntries.length === 1 ? 'entry' : 'entries'}
        </Text>
        {isCompleted && (
          <Badge colorScheme="gray" fontFamily="Hero-New" fontSize="xs">
            Labor Completed
          </Badge>
        )}
        <Tooltip
          label={
            sortDesc
              ? 'Showing newest first — tap to reverse'
              : 'Showing oldest first — tap to reverse'
          }
          hasArrow>
          <Button
            size="xs"
            variant="ghost"
            colorScheme="gray"
            fontFamily="Hero-New"
            ml="auto"
            onClick={handleSortToggle}>
            {sortDesc ? '↓ Newest' : '↑ Oldest'}
          </Button>
        </Tooltip>
      </Flex>

      {/* Timeline entries */}
      <Flex direction="column" flex={1} minH={0} overflowY="auto" gap={2} p={3}>
        {sortedEntries.length === 0 ? (
          <Flex direction="column" align="center" justify="center" flex={1} gap={3} py={8}>
            <Text
              fontFamily="Hero-New"
              fontSize="md"
              color="gray.400"
              _dark={{ color: 'gray.500' }}
              textAlign="center">
              No entries yet
            </Text>
            {isActive && (
              <Button
                colorScheme="pink"
                fontFamily="Hero-New"
                size="md"
                onClick={handleNewEntryAction}>
                + New Entry
              </Button>
            )}
          </Flex>
        ) : (
          sortedEntries.map(entry => (
            <EntryCard key={entry.id} entry={entry} onSelect={() => selectEntry(entry.id)} />
          ))
        )}
      </Flex>

      {/* Footer: New Entry + Report Birth */}
      {(isActive || isCompleted) && (
        <Flex
          direction="column"
          gap={2}
          px={3}
          pb={3}
          pt={2}
          borderTop="1px solid"
          borderColor="gray.100"
          bg="white"
          shrink={0}
          _dark={{ bg: 'gray.800', borderTopColor: 'whiteAlpha.100' }}>
          {/* Time range hint after delivery */}
          {isCompleted && laborOnsetAt && (
            <Alert status="info" borderRadius={6} py={1.5} fontSize="xs">
              <AlertIcon boxSize={3} />
              Labor completed. New entries are saved as Post-Birth Notes and are not backdated into
              the labor timeline.
            </Alert>
          )}

          <Flex gap={2} flexWrap="wrap">
            {(isActive || isCompleted) && (
              <Button
                flex={1}
                minW="120px"
                colorScheme="pink"
                variant={isCompleted ? 'outline' : 'solid'}
                fontFamily="Hero-New"
                size="md"
                onClick={handleNewEntryAction}>
                {isCompleted ? '+ Post-Birth Note' : '+ New Entry'}
              </Button>
            )}
            {/* §4.1.1: Report Birth — persistent footer button during active labor */}
            {isActive && !minimumDeliveryReport && (
              <Button
                flex={1}
                minW="140px"
                colorScheme="pink"
                fontFamily="Hero-New"
                fontWeight={700}
                size="md"
                boxShadow="md"
                onClick={handleOpenReportBirth}>
                Report Birth
              </Button>
            )}
          </Flex>
        </Flex>
      )}

      <ReportBirthModal isOpen={reportBirthOpen} onClose={() => setReportBirthOpen(false)} />
    </Flex>
  )
}
